import { Fragment, useCallback, useState } from 'react';
import { api, type Llamada, type Servicio, type SpanTraza, type Vital } from '../api';
import { etiquetaDeCanal } from '../canales';
import { Cascada } from './Cascada';
import { OjoIcono } from './OjoIcono';
import { fechaYHora } from '../fechas';
import type { SaludHttp } from '../api';
import { calificarPorReparto, etiquetaDeVital, formatearVital, REFERENCIA } from '../vitals';
import { Tabla } from './Tabla';
import { agruparSaludPorRuta } from '../salud-http';
import { detalleDeRuta } from '../spans';

/**
 * Salud de rendimiento: lo que vive el usuario y lo que hace el backend.
 *
 * Van juntos porque la pregunta es una sola. Un LCP malo con un backend rápido
 * apunta al renderizado; un LCP malo con el p95 de la API por las nubes apunta
 * al backend. Separarlos en dos pantallas obliga a hacer esa correlación de
 * memoria.
 */

export function Rendimiento({
  vitals,
  servicios,
  http,
  advertencia,
  fuente,
  desde,
  canal,
  trazaAbierta,
  spans,
  onAbrirTraza,
}: {
  vitals: Vital[];
  servicios: Servicio[];
  /** Salud por codigo de estado. Ausente si no hay histogramas todavia. */
  http?: SaludHttp[];
  advertencia?: string;
  fuente?: string;
  /** Inicio de la ventana, para pedir las llamadas de una ruta. */
  desde: string;
  canal?: string;
  trazaAbierta: string | null;
  spans: SpanTraza[];
  onAbrirTraza: (traceId: string) => void;
}) {
  const saludPorRuta = agruparSaludPorRuta(http);

  const [rutaAbierta, setRutaAbierta] = useState<string | null>(null);
  const [llamadas, setLlamadas] = useState<Llamada[]>([]);
  const [estado, setEstado] = useState<'pidiendo' | 'listo' | 'fuera_de_retencion' | 'error'>(
    'listo'
  );

  const abrirRuta = useCallback(
    async (clave: string, servicio: string, ruta: string) => {
      if (rutaAbierta === clave) {
        setRutaAbierta(null);
        setLlamadas([]);
        return;
      }
      setRutaAbierta(clave);
      setLlamadas([]);
      setEstado('pidiendo');
      try {
        const datos = await api.llamadas(servicio, ruta, desde, canal);
        setLlamadas(datos.llamadas);
        setEstado(datos.fuera_de_retencion ? 'fuera_de_retencion' : 'listo');
      } catch {
        // Que no se puedan traer los ejemplos no puede tapar la tabla: el
        // percentil que se estaba mirando sigue siendo válido.
        setEstado('error');
      }
    },
    [rutaAbierta, desde, canal]
  );

  return (
    <section className="rendimiento">
      <h3>Lo que vive el usuario</h3>
      {vitals.length === 0 ? (
        <p className="vacio">
          Todavía no llegaron Core Web Vitals. Se reportan solos desde el SDK web al cargarse una
          página.
        </p>
      ) : (
        <div className="vitals">
          {vitals.map((v) => {
            const calificacion = calificarPorReparto(v.buenos_pct, v.a_mejorar_pct);
            const referencia = REFERENCIA[v.vital];
            return (
              <div key={v.vital} className={`vital ${calificacion}`}>
                <div className="vital-cabecera">
                  <strong>{etiquetaDeVital(v.vital)}</strong>
                  <span className="tenue">{referencia?.bueno}</span>
                </div>
                <div className="vital-valor">{formatearVital(v.vital, v.p75)}</div>
                <div className="tenue">p75 · {v.muestras} muestras</div>

                {/* Reparto bueno / a mejorar / malo, que es como Google califica. */}
                <div className="reparto" title={`${v.buenos_pct}% bueno, ${v.a_mejorar_pct}% a mejorar, ${v.malos_pct}% malo`}>
                  <span className="bueno" style={{ width: `${v.buenos_pct}%` }} />
                  <span className="medio" style={{ width: `${v.a_mejorar_pct}%` }} />
                  <span className="malo" style={{ width: `${v.malos_pct}%` }} />
                </div>
                <p className="vital-que-es">{referencia?.que_es}</p>
              </div>
            );
          })}
        </div>
      )}

      <h3 className="con-espacio">Lo que hace el backend</h3>
      {advertencia ? (
        <p className="advertencia">{advertencia}</p>
      ) : (
        fuente === 'metricas' && (
          <p className="nota-fuente">
            Percentiles calculados sobre histogramas del 100% del tráfico, antes del muestreo. El
            p99 es una estimación: sirve la tendencia, no el milisegundo.
          </p>
        )
      )}

      {servicios.length === 0 ? (
        <p className="vacio">Sin llamadas registradas en la ventana.</p>
      ) : (
        <Tabla>
          <thead>
            <tr>
              <th>Ruta</th>
              <th>Servicio</th>
              <th>Canal</th>
              <th className="num">Llamadas</th>
              <th className="num">Errores</th>
              <th className="num">p50</th>
              <th className="num">p95</th>
              <th className="num">p99</th>
              <th className="num" title="5xx: el servicio se rompió">5xx</th>
              <th className="num" title="4xx que este cliente no declaró como operación normal">4xx</th>
              <th
                className="num"
                title="4xx que este cliente declaró como operación normal: el servicio evaluó y respondió que no. No son incidente, pero conviene verlos"
              >
                negocio
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {servicios.map((s) => {
              const clave = `${s.servicio}:${s.ruta}:${s.canal ?? ''}`;
              const abierta = rutaAbierta === clave;
              return (
              <Fragment key={clave}>
              <tr className={abierta ? 'seleccionada' : ''}>
                <td><code title={detalleDeRuta(s.ruta, s.urls)}>{s.ruta}</code></td>
                <td>{s.servicio}</td>
                <td>{s.canal ? <span className={`canal ${s.canal}`}>{etiquetaDeCanal(s.canal)}</span> : '—'}</td>
                <td className="num">{s.llamadas}</td>
                <td className={`num ${Number(s.errores) > 0 ? 'malo' : ''}`}>{s.errores}</td>
                <td className="num">{s.p50_ms} ms</td>
                <td className="num destacado">{s.p95_ms} ms</td>
                <td className="num">{s.p99_ms} ms</td>
                <td className={`num ${claseTasa(saludPorRuta.get(s.ruta)?.servidor, 1)}`}>
                  {textoTasa(saludPorRuta.get(s.ruta)?.servidor)}
                </td>
                <td className={`num ${claseTasa(saludPorRuta.get(s.ruta)?.contrato, 0.5)}`}>
                  {textoTasa(saludPorRuta.get(s.ruta)?.contrato)}
                </td>
                {/* Sin color: no es una alarma. Es cuánta gente recibió un no,
                    que es información de negocio y no de salud del servicio. */}
                <td className="num">{saludPorRuta.get(s.ruta)?.negocio || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="icono-ojo"
                    onClick={() => void abrirRuta(clave, s.servicio, s.ruta)}
                    aria-expanded={abierta}
                    aria-label={abierta ? 'Esconder las llamadas' : 'Ver llamadas de ejemplo'}
                    title={abierta ? 'Esconder las llamadas' : 'Ver llamadas de ejemplo'}
                  >
                    <OjoIcono abierto={abierta} />
                  </button>
                </td>
              </tr>
              {abierta && (
                <tr className="detalle-traza">
                  <td colSpan={12}>
                    <div className="traza">
                      <h4>Llamadas más lentas</h4>
                      <Llamadas
                        estado={estado}
                        llamadas={llamadas}
                        trazaAbierta={trazaAbierta}
                        spans={spans}
                        onAbrirTraza={onAbrirTraza}
                      />
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </Tabla>
      )}
    </section>
  );
}

/**
 * Las llamadas de ejemplo de una ruta, y la cascada de la que se elija.
 *
 * Ordenadas de la más lenta a la menos, que es el orden en que salen del
 * servidor y no un capricho de la vista: a esta lista se llega desde un p95
 * alto, así que la pregunta es «mostrame una de las lentas», no «qué pasó
 * recién».
 */
/**
 * Sin dato no se pinta nada: un cero donde no hubo medicion se lee como «no
 * falla», y es la misma forma de mentir que se evita en el resto del panel.
 */
function textoTasa(tasa: number | undefined): string {
  if (tasa === undefined) return '—';
  if (tasa === 0) return '0';
  return `${tasa < 0.1 ? '<0,1' : tasa.toFixed(1).replace('.', ',')}%`;
}

/** El umbral entra por parametro porque no es el mismo para 5xx que para 4xx. */
function claseTasa(tasa: number | undefined, umbral: number): string {
  if (tasa === undefined || tasa === 0) return '';
  return tasa >= umbral ? 'malo' : 'ojo';
}

function Llamadas({
  estado,
  llamadas,
  trazaAbierta,
  spans,
  onAbrirTraza,
}: {
  estado: 'pidiendo' | 'listo' | 'fuera_de_retencion' | 'error';
  llamadas: Llamada[];
  trazaAbierta: string | null;
  spans: SpanTraza[];
  onAbrirTraza: (traceId: string) => void;
}) {
  if (estado === 'pidiendo') return <p className="vacio">Buscando llamadas…</p>;
  if (estado === 'error') return <p className="error">No se pudieron traer las llamadas.</p>;
  if (estado === 'fuera_de_retencion') {
    return (
      <p className="vacio">
        La ventana pedida es más vieja que la retención de trazas: los percentiles de arriba
        siguen valiendo, pero ya no se guardan las llamadas que los formaron.
      </p>
    );
  }
  if (llamadas.length === 0) {
    return <p className="vacio">Sin llamadas guardadas para esta ruta en la ventana.</p>;
  }

  return (
    <>
      <ul className="llamadas">
        {llamadas.map((l) => {
          const abierta = trazaAbierta === l.trace_id;
          const fallo = l.estado === 'Error';
          return (
            <li key={l.trace_id}>
              <button
                type="button"
                className={`llamada ${abierta ? 'activa' : ''}`}
                onClick={() => onAbrirTraza(l.trace_id)}
                aria-expanded={abierta}
              >
                <span className="llamada-hora">
                  {fechaYHora(l.ts)}
                </span>
                <span className={`resultado ${fallo ? 'error' : 'ok'}`}>
                  {fallo ? 'ERROR' : 'OK'}
                </span>
                {l.canal && <span className={`canal ${l.canal}`}>{etiquetaDeCanal(l.canal)}</span>}
                <span className="llamada-duracion">{Math.round(l.duracion_ms)} ms</span>
                <OjoIcono abierto={abierta} />
              </button>
              {/* La cascada cuelga de su propia llamada y no del final de la
                  lista: abriendo la primera de diez, al final quedaba pasadas
                  las otras nueve y había que ir a buscarla. Es el mismo
                  comportamiento que la tabla de vistas de más arriba, donde el
                  detalle sale pegado a la fila que se abrió.

                  Una sola a la vez: abrir la siguiente reemplaza la anterior.
                  Varias abiertas obligan a desplazarse para comparar dos, que
                  es justamente lo que no se puede hacer con cascadas
                  apiladas. */}
              {abierta && (
                <div className="cascada-de-llamada">
                  <Cascada spans={spans} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
